/**
 * Error Boundary Component
 *
 * Catches React errors and reports them to Firebase
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';

/**
 * The boundary wraps LanguageProvider (so it can catch its errors too), which
 * means it can't use the useLanguage hook. Read the saved language directly.
 * Its few strings live here rather than in locales/ so the marketing pages,
 * which it also wraps, do not pull in every UI string.
 */
const MESSAGES = {
  tr: {
    'errorBoundary.title': 'Bir şeyler ters gitti',
    'errorBoundary.body': 'Bir hata oluştu. Sayfayı yenileyin ya da tekrar deneyin.',
    'errorBoundary.reload': 'Sayfayı yenile',
    'errorBoundary.details': 'Hata ayrıntıları (yalnızca geliştirme modunda)',
    'common.retry': 'Tekrar Dene'
  },
  en: {
    'errorBoundary.title': 'Something went wrong',
    'errorBoundary.body': 'An error occurred. Refresh the page or try again.',
    'errorBoundary.reload': 'Reload page',
    'errorBoundary.details': 'Error details (development only)',
    'common.retry': 'Retry'
  }
};
type MessageKey = keyof typeof MESSAGES.en;

function translate(key: MessageKey): string {
  let language: keyof typeof MESSAGES = 'en';
  try {
    const saved = localStorage.getItem('language') ?? navigator.language.toLowerCase().split('-')[0];
    if (saved === 'tr') language = 'tr';
  } catch {
    // storage unavailable: fall back to English
  }
  return MESSAGES[language][key];
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught error:', error, errorInfo);

    // Report to Firebase
    // Firebase loads on demand; it is not part of the marketing pages' bundle.
    import('../config/firebase')
      .then(({ reportError }) =>
        reportError(error, {
          componentStack: errorInfo.componentStack,
          errorBoundary: true
        })
      )
      .catch(() => {});
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h1 style={{ fontSize: '24px', marginBottom: '16px' }}>
            {translate('errorBoundary.title')}
          </h1>
          <p style={{ color: '#666', marginBottom: '24px' }}>
            {translate('errorBoundary.body')}
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {translate('common.retry')}
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {translate('errorBoundary.reload')}
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: '24px', textAlign: 'left', maxWidth: '600px' }}>
              <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                {translate('errorBoundary.details')}
              </summary>
              <pre style={{
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px'
              }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// Export as GlobalErrorBoundary for backwards compatibility
export const GlobalErrorBoundary = ErrorBoundary;
